/**
 * Composable per gestire il pattern standard di ricerca con paginazione e ordinamento
 *
 * @description
 * Astrae la logica comune di tutte le pagine di ricerca:
 * - Gestione stato ricerca (risultati, totale, loading)
 * - Handler per ordinamento e paginazione
 * - Trigger search con reset automatico della pagina se cambiano i filtri
 * - Integrazione con useFilterHandler e useLoadingState
 *
 * @example
 * ```typescript
 * const { dataPagination, dataOrder, dataFilters, buildOdataQuery, toQuery } = useFilterHandler({...});
 * const { withLoading } = useLoadingState();
 *
 * const {
 *   searchResult,
 *   searchResultTotal,
 *   triggerSearch,
 *   onSortChange,
 *   onPaginationPerPageChange,
 *   onPaginationPageChange
 * } = useDefaultSearch({
 *   entityKey: ENTITIES.ACTIVITIES, // Genera automaticamente messaggio errore
 *   dataPagination,
 *   dataOrder,
 *   dataFilters,
 *   buildOdataQuery,
 *   toQuery,
 *   withLoading,
 *   searchFn: (query: string) => myService.getData(query)
 * });
 *
 * // Chiamare in onMounted
 * onMounted(async () => {
 *   await triggerSearch();
 * });
 * ```
 */

import { ref } from 'vue';
import type { Ref } from 'vue';
import type { OrderOperatorType } from '@/mixins/filterHandler/types/operators';
import type { ODataResponse } from '@/lib/models/common';
import { useNotification } from '@/composables/useNotification';
import type { EntityKey } from '@/lib/constants/entityPermissions';
import { getEntityPermissions } from '@/lib/constants/entityPermissions';

/**
 * Configurazione per useDefaultSearch
 */
export interface UseDefaultSearchConfig<T> {
	/** Chiave dell'entità per generazione automatica messaggi di errore */
	entityKey: EntityKey;
	/** Oggetto pagination da useFilterHandler */
	dataPagination: {
		page: number;
		perPage: number;
	};
	/** Oggetto order da useFilterHandler (con metodo changeOrder) */
	dataOrder: {
		changeOrder: (propertyName: string, direction: OrderOperatorType) => void;
	};
	/** Oggetto filters da useFilterHandler */
	dataFilters: any;
	/** Funzione buildOdataQuery da useFilterHandler */
	buildOdataQuery: () => string;
	/** Funzione toQuery da useFilterHandler per sync URL */
	toQuery: () => void;
	/** Funzione fromQuery da useFilterHandler (chiamato automaticamente alla prima ricerca) */
	fromQuery: () => void;
	/** Funzione withLoading da useLoadingState */
	withLoading: <R>(fn: () => Promise<R>, operation?: string) => Promise<R>;
	/**
	 * Funzione di ricerca che esegue la chiamata API
	 * NOTA: ODataResponse<T> ha value: T[], quindi passare il tipo dell'elemento (es: Activity, non Activity[])
	 */
	searchFn: (query: string) => Promise<ODataResponse<T>>;
}

/**
 * Valore di ritorno del composable
 */
export interface UseDefaultSearchReturn<T> {
	/** Array dei risultati della ricerca */
	searchResult: Ref<T[]>;
	/** Totale dei risultati (per paginazione) */
	searchResultTotal: Ref<number | null>;
	/** Funzione per triggerare la ricerca (con reset pagina se filtri cambiano) */
	triggerSearch: () => Promise<void>;
	/** Handler per cambio ordinamento */
	onSortChange: (propertyName: string, direction: OrderOperatorType) => void;
	/** Handler per cambio numero elementi per pagina */
	onPaginationPerPageChange: (val: number) => void;
	/** Handler per cambio pagina corrente */
	onPaginationPageChange: (val: number) => void;
}

/**
 * Composable per gestione standard delle pagine di ricerca
 *
 * Gestisce automaticamente:
 * - State per risultati e totale
 * - Query OData building
 * - Loading state tramite withLoading
 * - Reset pagina se cambiano i filtri
 * - Handler per sort e paginazione
 *
 * @template T - Tipo degli item nei risultati
 */
export function useDefaultSearch<T = any>(
	config: UseDefaultSearchConfig<T>,
): UseDefaultSearchReturn<T> {
	const {
		entityKey,
		dataPagination,
		dataOrder,
		dataFilters,
		buildOdataQuery,
		toQuery,
		fromQuery,
		withLoading,
		searchFn,
	} = config;

	// Composables
	const { error: notifyError } = useNotification();

	// Genera messaggio di errore basato sull'entità
	const { displayName } = getEntityPermissions(entityKey);
	const errorMessage = `${displayName}: Errore durante il recupero dei dati!`;

	// Debug mode per logging dettagliato (solo in DEV_MODE)
	const isDevMode = process.env.DEV_MODE === 'true';

	// State interno
	const query = ref('');
	const searchResult = ref<T[]>([]) as Ref<T[]>;
	const searchResultTotal = ref<number | null>(null);
	const oldFilter = ref('');
	const isFirstSearch = ref(true);

	/**
	 * Esegue la ricerca principale
	 */
	const search = async (): Promise<void> => {
		try {
			await withLoading(async () => {
				if (isDevMode) {
					console.debug('🔍 [useDefaultSearch] search() - START');
				}
				toQuery(); // Sync URL
				query.value = buildOdataQuery();
				if (isDevMode) {
					console.debug('  🔗 OData query:', query.value);
				}
				const result = await searchFn(query.value);
				searchResult.value = result.value;
				searchResultTotal.value = result['@odata.count'] || 0;
				oldFilter.value = JSON.stringify(dataFilters);
				if (isDevMode) {
					console.debug('  ✅ Results count:', searchResultTotal.value);
					console.debug('🔍 [useDefaultSearch] search() - END');
				}
			});
		} catch (error) {
			console.error('❌ ERROR useDefaultSearch.search:', error);
			notifyError(errorMessage);
			searchResult.value = [];
		}
	};

	/**
	 * Trigger search con reset automatico pagina se filtri cambiano
	 *
	 * IMPORTANTE: Alla prima chiamata, garantisce che fromQuery() sia stato eseguito
	 * per caricare i filtri dall'URL prima di fare la ricerca.
	 */
	const triggerSearch = async (): Promise<void> => {
		if (isDevMode) {
			console.debug('🚀 [useDefaultSearch] triggerSearch() - START');
			console.debug('  🎯 First search:', isFirstSearch.value);
		}
		// Prima ricerca: assicura che i filtri siano stati caricati dall'URL
		if (isFirstSearch.value) {
			if (isDevMode) {
				console.debug('  ⬇️ Calling fromQuery() to ensure URL params are loaded...');
			}
			fromQuery(); // Safe: idempotente, non fa danni se già chiamato da useFilterHandler
			isFirstSearch.value = false;
		}

		const currentFilterState = JSON.stringify(dataFilters);
		if (currentFilterState !== oldFilter.value) {
			if (isDevMode) {
				console.debug('  🔄 Filters changed - Resetting page to 1');
			}
			dataPagination.page = 1;
		}
		if (isDevMode) {
			console.debug('  📄 Current page:', dataPagination.page);
			console.debug('🚀 [useDefaultSearch] triggerSearch() - Calling search()...');
		}
		await search();
	};

	/**
	 * Handler per cambio ordinamento
	 */
	const onSortChange = (propertyName: string, direction: OrderOperatorType): void => {
		dataOrder.changeOrder(propertyName, direction);
		search();
	};

	/**
	 * Handler per cambio numero elementi per pagina
	 */
	const onPaginationPerPageChange = (val: number): void => {
		dataPagination.perPage = val;
		search();
	};

	/**
	 * Handler per cambio pagina corrente
	 */
	const onPaginationPageChange = (val: number): void => {
		dataPagination.page = val;
		search();
	};

	return {
		searchResult,
		searchResultTotal,
		triggerSearch,
		onSortChange,
		onPaginationPerPageChange,
		onPaginationPageChange,
	};
}
